import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Eye, EyeOff, CreditCard, Lock, ArrowRight, Sun, Moon, Shield, ArrowLeft, Mail, KeyRound, Check, Smartphone, Clock } from 'lucide-react';
import { api } from '../api';
import './LoginPage.css';

const LOGO_URL = 'https://acesso.ihubs.app/api/uploads/logo/acesso/logo-light-1773085624457.png';

function LoginPage({ onLogin, theme, setTheme }) {
  const [cpf, setCpf] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpDigits, setTotpDigits] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  // Device verification state
  const [needsDeviceVerification, setNeedsDeviceVerification] = useState(false);
  const [deviceEmail, setDeviceEmail] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deviceCode, setDeviceCode] = useState(['', '', '', '', '', '']);
  const deviceCodeRefs = useRef([]);

  // Reset password state
  const [resetStep, setResetStep] = useState(null);
  const [resetCpf, setResetCpf] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState(['', '', '', '', '', '']);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const resetCodeRefs = useRef([]);

  // SMS countdown timer
  const [smsCountdown, setSmsCountdown] = useState(0);
  const countdownRef = useRef(null);

  const startCountdown = useCallback((seconds = 600) => {
    setSmsCountdown(seconds);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setSmsCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, []);

  const formatCpf = (value) => {
    const nums = value.replace(/\D/g, '').slice(0, 11);
    return nums
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const handleCpfChange = (e) => {
    setCpf(formatCpf(e.target.value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const rawCpf = cpf.replace(/\D/g, '');
      const result = await onLogin(rawCpf, password);
      if (result && result.requires2FA) {
        setNeeds2FA(true);
        setTotpDigits(['', '', '', '', '', '']);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else if (result && result.requiresDeviceVerification) {
        setNeedsDeviceVerification(true);
        setDeviceEmail(result.email || '');
        setDeviceName(result.device || 'Novo dispositivo');
        setDeviceCode(['', '', '', '', '', '']);
        setError('');
        if (!result.alreadySent) startCountdown(3600);
        else startCountdown(3000); // already sent = assume some time passed
        setTimeout(() => deviceCodeRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      setError(err.error || 'Usuário ou senha incorretos');
    } finally {
      setIsLoading(false);
    }
  };

  // Device verification handlers
  const handleDeviceCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...deviceCode];
    newCode[index] = value.slice(-1);
    setDeviceCode(newCode);
    if (value && index < 5) deviceCodeRefs.current[index + 1]?.focus();
  };

  const handleDeviceCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !deviceCode[index] && index > 0) {
      deviceCodeRefs.current[index - 1]?.focus();
    }
  };

  const handleDeviceCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...deviceCode];
      for (let i = 0; i < 6; i++) newCode[i] = pasted[i] || '';
      setDeviceCode(newCode);
      deviceCodeRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  const handleDeviceCodeSubmit = async (e) => {
    e.preventDefault();
    const code = deviceCode.join('');
    if (code.length !== 6) return;
    setError('');
    setIsLoading(true);
    try {
      const rawCpf = cpf.replace(/\D/g, '');
      await onLogin(rawCpf, password, undefined, code);
    } catch (err) {
      setError(err.error || 'Código inválido');
      setDeviceCode(['', '', '', '', '', '']);
      setTimeout(() => deviceCodeRefs.current[0]?.focus(), 100);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-submit device code
  useEffect(() => {
    if (needsDeviceVerification && deviceCode.every(d => d !== '') && !isLoading) {
      handleDeviceCodeSubmit({ preventDefault: () => {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceCode, needsDeviceVerification]);

  const handleTotpDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...totpDigits];
    newDigits[index] = value.slice(-1);
    setTotpDigits(newDigits);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleTotpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !totpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleTotpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newDigits = [...totpDigits];
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pasted[i] || '';
      }
      setTotpDigits(newDigits);
      const focusIdx = Math.min(pasted.length, 5);
      inputRefs.current[focusIdx]?.focus();
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    const code = totpDigits.join('');
    if (code.length !== 6) return;

    setError('');
    setIsLoading(true);
    try {
      const rawCpf = cpf.replace(/\D/g, '');
      await onLogin(rawCpf, password, code);
    } catch (err) {
      setError(err.error || 'Código inválido');
      setTotpDigits(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-submit when all 6 digits are filled
  useEffect(() => {
    if (needs2FA && totpDigits.every(d => d !== '') && !isLoading) {
      const code = totpDigits.join('');
      if (code.length === 6) {
        handleTotpSubmit({ preventDefault: () => {} });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totpDigits, needs2FA]);

  const handleBack = () => {
    setNeeds2FA(false);
    setNeedsDeviceVerification(false);
    setTotpDigits(['', '', '', '', '', '']);
    setDeviceCode(['', '', '', '', '', '']);
    setError('');
    setSmsCountdown(0);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  // Reset password handlers
  const openReset = (e) => {
    e.preventDefault();
    setResetStep('cpf');
    setResetCpf('');
    setResetEmail('');
    setResetCode(['', '', '', '', '', '']);
    setResetNewPassword('');
    setResetConfirmPassword('');
    setError('');
  };

  const handleResetBack = () => {
    if (resetStep === 'code') setResetStep('cpf');
    else if (resetStep === 'password') setResetStep('code');
    else { setResetStep(null); setError(''); }
  };

  const handleResetRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const rawCpf = resetCpf.replace(/\D/g, '');
      const result = await api.requestPasswordReset(rawCpf);
      setResetEmail(result.email);
      setResetStep('code');
      setResetCode(['', '', '', '', '', '']);
      if (!result.alreadySent) startCountdown(3600);
      else startCountdown(3000);
      setTimeout(() => resetCodeRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.error || 'Erro ao enviar código');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...resetCode];
    newCode[index] = value.slice(-1);
    setResetCode(newCode);
    if (value && index < 5) resetCodeRefs.current[index + 1]?.focus();
  };

  const handleResetCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !resetCode[index] && index > 0) {
      resetCodeRefs.current[index - 1]?.focus();
    }
  };

  const handleResetCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...resetCode];
      for (let i = 0; i < 6; i++) newCode[i] = pasted[i] || '';
      setResetCode(newCode);
      resetCodeRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  const handleResetCodeSubmit = (e) => {
    e.preventDefault();
    if (resetCode.some(d => d === '')) return;
    setError('');
    setResetStep('password');
  };

  // Auto-advance from code to password step
  useEffect(() => {
    if (resetStep === 'code' && resetCode.every(d => d !== '') && !isLoading) {
      setError('');
      setResetStep('password');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetCode, resetStep]);

  const handleResetNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (resetNewPassword.length < 4) {
      setError('A senha deve ter no mínimo 4 caracteres');
      return;
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    setIsLoading(true);
    try {
      const rawCpf = resetCpf.replace(/\D/g, '');
      const code = resetCode.join('');
      await api.verifyPasswordReset(rawCpf, code, resetNewPassword);
      setResetStep('done');
    } catch (err) {
      setError(err.error || 'Erro ao redefinir senha');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`login-container ${theme}`}>
      <div className="bg-glow" />

      <button
        className="theme-toggle"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        title={theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
      >
        {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
      </button>

      <div className="login-card">
        <div className="card-line" />

        <div className="logo-container">
          <img src={LOGO_URL} alt="Logo" className="logo" />
        </div>

        {needsDeviceVerification ? (
          <>
            <div className="form-header">
              <div className="totp-icon-wrap" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>
                <Smartphone size={24} />
              </div>
              <h2 className="form-title">Novo dispositivo detectado</h2>
              <p className="form-subtitle">
                Digite o código enviado por e-mail para <strong>{deviceEmail}</strong>
              </p>
              {smsCountdown > 0 && (
                <div className="sms-timer">
                  <Clock size={13} />
                  <span>Código expira em {String(Math.floor(smsCountdown / 60)).padStart(2, '0')}:{String(smsCountdown % 60).padStart(2, '0')}</span>
                </div>
              )}
              <p className="form-subtitle" style={{ marginTop: 4, fontSize: '0.7rem', opacity: 0.7 }}>
                Verifique também a caixa de spam.
              </p>
            </div>

            {error && <div className="login-error">{error}</div>}

            <form onSubmit={handleDeviceCodeSubmit} className="login-form">
              <div className="totp-inputs" onPaste={handleDeviceCodePaste}>
                {deviceCode.map((digit, i) => (
                  <React.Fragment key={i}>
                    <input
                      ref={(el) => (deviceCodeRefs.current[i] = el)}
                      type="text" inputMode="numeric" maxLength={1}
                      className="totp-digit" value={digit}
                      onChange={(e) => handleDeviceCodeChange(i, e.target.value)}
                      onKeyDown={(e) => handleDeviceCodeKeyDown(i, e)}
                      autoFocus={i === 0}
                    />
                    {i === 2 && <span className="totp-separator" />}
                  </React.Fragment>
                ))}
              </div>

              <button type="submit" className={`submit-btn ${isLoading ? 'loading' : ''}`}
                disabled={isLoading || deviceCode.some(d => d === '')}>
                {isLoading ? <div className="spinner" /> : <><span>Verificar</span><Mail size={18} /></>}
              </button>

              <button type="button" className="back-btn" onClick={handleBack}>
                <ArrowLeft size={15} /><span>Voltar ao login</span>
              </button>
            </form>
          </>
        ) : resetStep ? (
          <>
            {/* RESET STEP 1: Enter CPF */}
            {resetStep === 'cpf' && (
              <>
                <div className="form-header">
                  <div className="totp-icon-wrap">
                    <KeyRound size={24} />
                  </div>
                  <h2 className="form-title">Recuperar senha</h2>
                  <p className="form-subtitle">
                    Informe seu CPF para receber o código por e-mail
                  </p>
                </div>

                {error && <div className="login-error">{error}</div>}

                <form onSubmit={handleResetRequestCode} className="login-form">
                  <div className={`input-group ${focusedField === 'resetCpf' ? 'focused' : ''}`}>
                    <div className="input-icon"><CreditCard size={18} /></div>
                    <input
                      type="text" placeholder="Seu CPF"
                      value={formatCpf(resetCpf)}
                      onChange={(e) => setResetCpf(formatCpf(e.target.value))}
                      onFocus={() => setFocusedField('resetCpf')}
                      onBlur={() => setFocusedField(null)}
                      required autoFocus
                    />
                  </div>

                  <button type="submit" className={`submit-btn ${isLoading ? 'loading' : ''}`} disabled={isLoading}>
                    {isLoading ? <div className="spinner" /> : <><span>Enviar código por e-mail</span><Mail size={18} /></>}
                  </button>

                  <button type="button" className="back-btn" onClick={() => { setResetStep(null); setError(''); }}>
                    <ArrowLeft size={15} /><span>Voltar ao login</span>
                  </button>
                </form>
              </>
            )}

            {/* RESET STEP 2: Enter SMS code */}
            {resetStep === 'code' && (
              <>
                <div className="form-header">
                  <div className="totp-icon-wrap" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>
                    <Mail size={24} />
                  </div>
                  <h2 className="form-title">Código de verificação</h2>
                  <p className="form-subtitle">
                    Digite o código enviado por e-mail para <strong>{resetEmail}</strong>
                  </p>
                  {smsCountdown > 0 && (
                    <div className="sms-timer">
                      <Clock size={13} />
                      <span>Código expira em {String(Math.floor(smsCountdown / 60)).padStart(2, '0')}:{String(smsCountdown % 60).padStart(2, '0')}</span>
                    </div>
                  )}
                  <p className="form-subtitle" style={{ marginTop: 4, fontSize: '0.7rem', opacity: 0.7 }}>
                    Verifique também a caixa de spam.
                  </p>
                </div>

                {error && <div className="login-error">{error}</div>}

                <form onSubmit={handleResetCodeSubmit} className="login-form">
                  <div className="totp-inputs" onPaste={handleResetCodePaste}>
                    {resetCode.map((digit, i) => (
                      <React.Fragment key={i}>
                        <input
                          ref={(el) => (resetCodeRefs.current[i] = el)}
                          type="text" inputMode="numeric" maxLength={1}
                          className="totp-digit" value={digit}
                          onChange={(e) => handleResetCodeChange(i, e.target.value)}
                          onKeyDown={(e) => handleResetCodeKeyDown(i, e)}
                          autoFocus={i === 0}
                        />
                        {i === 2 && <span className="totp-separator" />}
                      </React.Fragment>
                    ))}
                  </div>

                  <button type="submit" className="submit-btn" disabled={resetCode.some(d => d === '')}>
                    <span>Verificar código</span><ArrowRight size={18} />
                  </button>

                  <button type="button" className="back-btn" onClick={handleResetBack}>
                    <ArrowLeft size={15} /><span>Voltar</span>
                  </button>
                </form>
              </>
            )}

            {/* RESET STEP 3: New password */}
            {resetStep === 'password' && (
              <>
                <div className="form-header">
                  <div className="totp-icon-wrap" style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }}>
                    <Lock size={24} />
                  </div>
                  <h2 className="form-title">Nova senha</h2>
                  <p className="form-subtitle">
                    Crie uma nova senha para sua conta
                  </p>
                </div>

                {error && <div className="login-error">{error}</div>}

                <form onSubmit={handleResetNewPassword} className="login-form">
                  <div className={`input-group ${focusedField === 'newPass' ? 'focused' : ''}`}>
                    <div className="input-icon"><Lock size={18} /></div>
                    <input
                      type={showResetPassword ? 'text' : 'password'}
                      placeholder="Nova senha (mín. 4 caracteres)"
                      value={resetNewPassword}
                      onChange={(e) => setResetNewPassword(e.target.value)}
                      onFocus={() => setFocusedField('newPass')}
                      onBlur={() => setFocusedField(null)}
                      required autoFocus minLength={4}
                    />
                    <button type="button" className="toggle-password" onClick={() => setShowResetPassword(!showResetPassword)} tabIndex={-1}>
                      {showResetPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  <div className={`input-group ${focusedField === 'confirmPass' ? 'focused' : ''}`}>
                    <div className="input-icon"><Lock size={18} /></div>
                    <input
                      type={showResetPassword ? 'text' : 'password'}
                      placeholder="Confirme a nova senha"
                      value={resetConfirmPassword}
                      onChange={(e) => setResetConfirmPassword(e.target.value)}
                      onFocus={() => setFocusedField('confirmPass')}
                      onBlur={() => setFocusedField(null)}
                      required minLength={4}
                    />
                  </div>

                  <button type="submit" className={`submit-btn ${isLoading ? 'loading' : ''}`} disabled={isLoading}>
                    {isLoading ? <div className="spinner" /> : <><span>Redefinir senha</span><KeyRound size={18} /></>}
                  </button>

                  <button type="button" className="back-btn" onClick={handleResetBack}>
                    <ArrowLeft size={15} /><span>Voltar</span>
                  </button>
                </form>
              </>
            )}

            {/* RESET STEP 4: Success */}
            {resetStep === 'done' && (
              <>
                <div className="form-header">
                  <div className="totp-icon-wrap" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>
                    <Check size={24} />
                  </div>
                  <h2 className="form-title">Senha redefinida!</h2>
                  <p className="form-subtitle">
                    Sua senha foi alterada com sucesso. Faça login com a nova senha.
                  </p>
                </div>

                <div className="login-form">
                  <button type="button" className="submit-btn" onClick={() => { setResetStep(null); setError(''); }}>
                    <span>Voltar ao login</span><ArrowRight size={18} />
                  </button>
                </div>
              </>
            )}
          </>
        ) : !needs2FA ? (
          <>
            <div className="form-header">
              <h2 className="form-title">Acesse sua conta</h2>
              <p className="form-subtitle">
                Entre com suas credenciais para continuar
              </p>
            </div>

            {error && <div className="login-error">{error}</div>}

            <form onSubmit={handleSubmit} className="login-form">
              <div className={`input-group ${focusedField === 'cpf' ? 'focused' : ''}`}>
                <div className="input-icon">
                  <CreditCard size={18} />
                </div>
                <input
                  type="text"
                  placeholder="Seu CPF"
                  value={cpf}
                  onChange={handleCpfChange}
                  onFocus={() => setFocusedField('cpf')}
                  onBlur={() => setFocusedField(null)}
                  required
                />
              </div>

              <div className={`input-group ${focusedField === 'password' ? 'focused' : ''}`}>
                <div className="input-icon">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="form-options">
                <label className="remember-me">
                  <input type="checkbox" />
                  <span className="checkmark" />
                  <span>Lembrar de mim</span>
                </label>
                <a href="#forgot" className="forgot-link" onClick={openReset}>
                  Esqueceu a senha?
                </a>
              </div>

              <button
                type="submit"
                className={`submit-btn ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="spinner" />
                ) : (
                  <>
                    <span>Entrar</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="form-header">
              <div className="totp-icon-wrap">
                <Shield size={24} />
              </div>
              <h2 className="form-title">Verificação em 2 etapas</h2>
              <p className="form-subtitle">
                Digite o código de 6 dígitos do seu aplicativo autenticador
              </p>
            </div>

            {error && <div className="login-error">{error}</div>}

            <form onSubmit={handleTotpSubmit} className="login-form">
              <div className="totp-inputs" onPaste={handleTotpPaste}>
                {totpDigits.map((digit, i) => (
                  <React.Fragment key={i}>
                    <input
                      ref={(el) => (inputRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className="totp-digit"
                      value={digit}
                      onChange={(e) => handleTotpDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleTotpKeyDown(i, e)}
                      autoFocus={i === 0}
                    />
                    {i === 2 && <span className="totp-separator" />}
                  </React.Fragment>
                ))}
              </div>

              <button
                type="submit"
                className={`submit-btn ${isLoading ? 'loading' : ''}`}
                disabled={isLoading || totpDigits.some(d => d === '')}
              >
                {isLoading ? (
                  <div className="spinner" />
                ) : (
                  <>
                    <span>Verificar</span>
                    <Shield size={18} />
                  </>
                )}
              </button>

              <button type="button" className="back-btn" onClick={handleBack}>
                <ArrowLeft size={15} />
                <span>Voltar ao login</span>
              </button>
            </form>
          </>
        )}
      </div>

      <p className="login-footer">
        Protegido por criptografia de ponta a ponta
      </p>
    </div>
  );
}

export default LoginPage;
